import React, { useCallback } from 'react';

import { Flex } from '@chakra-ui/react';
import { Card, CardContent } from '@material-ui/core';
import { updatePostAction, toggleAddPostModal } from 'actions/addPost';
import DotsMoreComponent, { getPostMoreMenuItems } from 'components/Comment/CommentMore';
import Emoji from 'components/Emoji';
import { StyledAvatar } from 'components/avatars';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postDataInitialStateRabbit } from 'reducers/addPost';
import { selectFeedDataById } from 'selectors/feed';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { FeedCardMobileCSS, StyledBox, StyledTypography } from 'styles/common';

import { Public } from 'assets/assetsV1/iconComponents';
import { Praise } from 'assets/assetsV1/iconComponents/Praise';
import confettiSrc from 'assets/images/confetti.png';

import { POST_VISIBILITY_TYPES, LANDING_PAGE_TYPES, ADD_POST_MODAL_TYPES } from 'constants/ProjectConstants';
import { getTeamPageRoute, getTagDetailRoute, getHumanReadableDate } from 'constants/helper';
import { LockIcon } from 'constants/icons';
import { POST_EVENTS, sendPostEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showTaskDetails } from 'services/taskDetails';

import { Typography13, Typography15 } from 'uiToolkit/Typography';
import { TextParseComponent } from 'uiToolkitV1/TextParseComponent';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import CardHeaderWithBorder from './CardHeaderWithBorder';
import { CardHeaderWrapper, CardWrapper } from './common/FeedCard';

const CardHeader = ({ isKudos, fromEmployee, createdDate, visibilityType, OptionsComponent = null }) => (
  <CardHeaderWrapper>
    <Flex width="100%" alignItems="center">
      <Flex mr="16px">
        <Praise fill="transparent" />{' '}
      </Flex>
      <Flex flexDir="column">
        <TextBodyMedium bold>
          {isKudos ? 'Praise' : 'Feedback'} from {fromEmployee.fullName}
        </TextBodyMedium>
        <Flex>
          <TextLabel color="text.g2">{getHumanReadableDate(createdDate)}</TextLabel>
          <TextLabel sx={{ margin: '0 4px' }} color="text.g2">
            •
          </TextLabel>
          {visibilityType === POST_VISIBILITY_TYPES.private.name && (
            <LockIcon size="tiniestFixed" color={theme.palette.icons.grey2} rightGutter={0.5} />
          )}
          {visibilityType === POST_VISIBILITY_TYPES.global.name && (
            <Flex>
              <Public boxSize="16px" color="text.g2" />
              <TextLabel color="text.g2">Public</TextLabel>{' '}
            </Flex>
          )}
        </Flex>
      </Flex>
      {OptionsComponent ? (
        <StyledBox display="flex" alignItems="center" marginLeft="auto">
          {OptionsComponent}
        </StyledBox>
      ) : null}
    </Flex>
  </CardHeaderWrapper>
);

const Hashtag = React.memo(({ tagName }) => (
  <StyledBox
    padding="4px 8px"
    style={{ backgroundColor: '#FCEED3', borderRadius: '8px' }}
    rightGutter={1}
    bottomGutter={1}
  >
    <Typography13 color="#333333" style={{ display: 'inline', lineHeight: '28px' }}>
      {`#${tagName}`}
    </Typography13>
  </StyledBox>
));

const HashtagContainer = ({ competencies }) => (
  <StyledBox display="flex" flexWrap="wrap" justifyContent="center" topGutter={2}>
    {competencies.map((competency) => (
      <Hashtag key={competency.id} tagName={competency.displayName} />
    ))}
  </StyledBox>
);

const MentionedEmployeeAvatar = ({ src, displayName }) => (
  <StyledBox topGutter={2} display="flex" flexDirection="column" alignItems="center" minWidth="15ch" maxWidth="20ch">
    <StyledAvatar src={src} size="fixed48px" />
    <Typography13 topGutter={0.5} color="#333333" align="center">
      {displayName}
    </Typography13>
  </StyledBox>
);

const EmployeeAvatarContainer = ({ allEmployees, topGutter = 4 }) => (
  <StyledBox topGutter={topGutter} bottomGutter={4} display="flex" justifyContent="space-evenly" flexWrap="wrap">
    {allEmployees.map((emp) => (
      <MentionedEmployeeAvatar key={emp.uuid} src={emp.profile.profilePhoto} displayName={emp.metadata.displayName} />
    ))}
  </StyledBox>
);

const CardBody = React.memo(({ children, macrosRes, isKudos }) => {
  const allCompetencies = Object.values(macrosRes?.attributes?.orgTag ?? {});
  const allMentionedEmployees = Object.values(macrosRes?.employees ?? {});

  return (
    <CardBodyContainer $isKudos={isKudos}>
      <StyledBox display="flex" justifyContent="space-between" alignItems="flex-start">
        {isKudos ? <StyledImg src={confettiSrc} alt="confetti" width="20%" $left="-24px" /> : null}

        <StyledBox flex="1 1 auto">
          <Typography15
            color={isKudos ? theme.palette.text.green : theme.palette.text.orangeRed}
            weight="600"
            topGutter={3}
            align="center"
          >
            {isKudos ? (
              'Congratulations!!!'
            ) : (
              <>
                <Emoji label="muscle" symbol="💪" /> Sharpen your axe
              </>
            )}
          </Typography15>
          {/* <HashtagContainer competencies={allCompetencies} /> */}
        </StyledBox>
        {isKudos ? (
          <StyledImg
            src={confettiSrc}
            alt="confetti"
            width="20%"
            style={{ transform: 'rotate(-180deg)' }}
            $right="-24px"
          />
        ) : null}
      </StyledBox>

      <EmployeeAvatarContainer topGutter={isKudos ? 0 : 2} allEmployees={allMentionedEmployees} />

      {children}
    </CardBodyContainer>
  );
});

export function PostText({ className, text, macrosRes, onPostClick, ...textStyleProps }) {
  return (
    <StyledTypography className={className} variant="body2" onClick={onPostClick} {...textStyleProps}>
      <TextParseComponent text={text} macros={macrosRes} color={textStyleProps?.customColor} />
    </StyledTypography>
  );
}

function KudosAndFeedbackPostFeedItem({
  feedId,
  className,
  employee,
  pageType,
  renderLikeCommentsSectionComponent,
  ReasonHeader, // TODO: Later individual renders like this need to come as children, so that there is no hard binding like this
  MediaView,
  isKudos,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const data = useSelector((state) => selectFeedDataById(state, feedId)) || {};
  const {
    landingPage: landingPageDetails,
    fromEmployee,
    text,
    macrosRes,
    id,
    createdDate,
    media = [],
    contentState,
    actions,
    visibilityType,
  } = data;

  // Extract it out to separate custom hook
  const onPostClick = useCallback(
    (event) => {
      if (!landingPageDetails.ids || !landingPageDetails.ids.length === 0) return;
      // if bubbling is happeing and a child link is clicked then don't open panel
      if (event.target && event.target.tagName === 'A') return;
      if (landingPageDetails.type === LANDING_PAGE_TYPES.task) {
        sendPostEvent({ pageType, eventName: POST_EVENTS.openedTaskPanel });
        dispatch(showTaskDetails({ taskIds: landingPageDetails.ids, employee }));
      } else if (landingPageDetails.type === LANDING_PAGE_TYPES.team) {
        const teamId = landingPageDetails.ids[0];
        const teamName = landingPageDetails.data[teamId].name;
        history.push(getTeamPageRoute({ teamId, teamName }));
      } else if (landingPageDetails.type === LANDING_PAGE_TYPES.tag) {
        const tagId = landingPageDetails.ids[0];
        const tagName = landingPageDetails.data[tagId].name;
        history.push(getTagDetailRoute({ tagId, tagName }));
      }
    },
    [dispatch, JSON.stringify(landingPageDetails), history, pageType, JSON.stringify(employee)]
  );

  if (!Object.keys(data) === 0) return;

  const editActionData = actions.edit;

  function onEditClick() {
    const initialState = postDataInitialStateRabbit(
      contentState,
      media,
      id,
      visibilityType.name,
      macrosRes?.attributes?.sentiment
    );
    dispatch(updatePostAction(initialState));
    dispatch(toggleAddPostModal(isKudos ? ADD_POST_MODAL_TYPES.GIVE_KUDOS : ADD_POST_MODAL_TYPES.GIVE_FEEDBACK));
  }

  return (
    <CardWrapper>
      <StyledCard className={className || ''} elevation={0}>
        {typeof ReasonHeader === 'function' ? ReasonHeader({ onClick: onPostClick }) : ReasonHeader}
        <CardHeader
          isKudos={isKudos}
          fromEmployee={fromEmployee}
          createdDate={createdDate}
          visibilityType={visibilityType.name}
          OptionsComponent={
            loggedInEmployeeId === fromEmployee.id ? (
              <DotsMoreComponent
                menuItems={getPostMoreMenuItems({
                  toggleEditing: onEditClick,
                  isEditable: editActionData.canPerform,
                  cantEditReason: editActionData.reason,
                  visibilityType: null,
                })}
              />
            ) : null
          }
        />
        <StyledCardContent>
          <CardBody macrosRes={macrosRes} text={text} isKudos={isKudos}>
            <StyledPostText text={text} macrosRes={macrosRes} onPostClick={onPostClick} />
            <MediaView style={{ marginTop: '1rem' }} />
          </CardBody>
        </StyledCardContent>
        {renderLikeCommentsSectionComponent()}
      </StyledCard>
    </CardWrapper>
  );
}

const StyledCard = styled(Card)`
  ${FeedCardMobileCSS};
  overflow: inherit; // to make sure that the smileys are visible outside the parent content as well
`;

const StyledCardContent = styled(CardContent)`
  &.MuiCardContent-root {
    padding: 1rem 1.5rem 0;
  }
`;

const StyledPostText = styled(PostText)`
  cursor: default;
`;

const CardBodyContainer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  padding: 1.5rem;
  background: ${(props) =>
    props.$isKudos
      ? `linear-gradient(101.89deg,
        rgba(7, 255, 17, 0.04) 9.29%,
        rgba(255, 255, 255, 0) 58.97%,
        rgba(7, 255, 17, 0.04) 92.41%)`
      : `linear-gradient(
    101.89deg,
    rgba(255, 201, 7, 0.04) 9.29%,
    rgba(255, 255, 255, 0) 58.97%,
    rgba(255, 201, 7, 0.04) 92.41%
  )`};
`;

const StyledImg = styled.img`
  mix-blend-mode: darken;
  position: relative;
  top: -24px;
  left: ${(props) => props.$left};
  right: ${(props) => props.$right};
  flex: 0 0 auto;
`;

const StyledCardHeaderWithBorder = styled(CardHeaderWithBorder)`
  ${(props) =>
    props.$isFeedbackHeader &&
    css`
      padding-left: 0rem;
    `};
`;

export default KudosAndFeedbackPostFeedItem;
